import { forwardRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Column } from "@components/EditableDataTable/types/column";
import { useV2ListProductsForAccountQuery } from "@generated";
import BlueprintItemsTab, {
  TabRow,
  BlueprintItemsTabHandle,
} from "./BlueprintItemsTab";
import type { BlueprintDetail } from "../../common/api";

const tabColumns: Column[] = [
  { header: () => "Name", cell: (row) => row.name },
  { header: () => "MFR Part", cell: (row) => row.mfrPart },
  { id: "quantity", header: () => "Quantity", cell: (row) => row.quantity },
  { header: () => "Unit Price", cell: (row) => row.unitPrice },
];

const catalogColumns: ColumnDef<TabRow>[] = [
  { header: "Name", cell: (props) => props.row.original.name },
  { header: "MFR Part", accessorKey: "mfrPart" },
  { header: "SKU", accessorKey: "sku" },
  { header: "Unit Price", accessorKey: "unitPrice" },
];

const toTabRow = (item: any, idx: number): TabRow => ({
  id: String(item.id ?? item.lid ?? `p_${idx}`),
  name: item.attributes?.name || "",
  mfrPart: item.attributes?.["manufacturer-part-number"] || "",
  quantity: item.attributes?.quantity ?? 1,
  unitPrice: item.attributes?.["unit-price"] ?? "",
});

const toPayloadItem = (row: TabRow) => ({
  lid: crypto.randomUUID(),
  type: "project-products",
  attributes: {
    name: row.name,
    quantity: 1,
    "manufacturer-part-number": row.mfrPart,
    sku: row.sku,
    "unit-price": row.unitPrice,
  },
  relationships: {
    product: { data: { id: Number(row.id), type: "products" } },
  },
});

const ProductsTab = forwardRef<
  BlueprintItemsTabHandle,
  { blueprint: BlueprintDetail; refetch: () => void; readOnly?: boolean }
>(({ blueprint, refetch, readOnly }, ref) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useV2ListProductsForAccountQuery({
    filter: { name: search || undefined },
    page: { number: page, size: pageSize },
  });

  const catalogItems: TabRow[] = (data?.data ?? []).map((p) => ({
    id: String(p.id),
    name: p.attributes?.name || "",
    mfrPart: p.attributes?.["manufacturer-part-number"] || "",
    sku: p.attributes?.sku || "",
    unitPrice: p.attributes?.["unit-price"] ?? "",
  }));

  return (
    <BlueprintItemsTab
      ref={ref}
      blueprint={blueprint}
      refetch={refetch}
      payloadType="project-products"
      toTabRow={toTabRow}
      toPayloadItem={toPayloadItem}
      tabColumns={tabColumns}
      catalogColumns={catalogColumns}
      catalogItems={catalogItems}
      catalogLoading={isLoading}
      onCatalogSearch={(q) => {
        setSearch(q);
        setPage(1);
      }}
      catalogPagination={{
        currentPage: page,
        pageSize,
        totalPages: data?.meta?.["page-count"] ?? 1,
        totalRows: data?.meta?.["record-count"] ?? 0,
        setCurrentPage: setPage,
        setPageSize,
      }}
      itemLabel="Products"
      editableQuantity
      readOnly={readOnly}
    />
  );
});

ProductsTab.displayName = "ProductsTab";
export default ProductsTab;
